
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Calendar, Clock, User, Plus, Edit, Check, X } from "lucide-react";
import { Lead, Task } from '../../types/crm';

interface TaskManagerProps {
  leads: Lead[];
  onUpdateTasks: (leadId: number, tasks: Task[]) => void;
}

const TaskManager = ({ leads, onUpdateTasks }: TaskManagerProps) => {
  const [selectedLead, setSelectedLead] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'Medium' as 'High' | 'Medium' | 'Low',
    assignedTo: '',
    type: 'follow-up' as 'call' | 'email' | 'meeting' | 'proposal' | 'follow-up' | 'demo'
  });

  const getAllTasks = () => {
    const allTasks: (Task & { leadId: number; leadName: string })[] = [];
    leads.forEach(lead => {
      if (lead.tasks) {
        lead.tasks.forEach(task => {
          allTasks.push({
            ...task,
            leadId: lead.id,
            leadName: lead.companyName
          });
        });
      }
    });
    return allTasks.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  };

  const getTasksByStatus = (status: string) => {
    return getAllTasks().filter(task => task.status === status);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleCreateTask = () => {
    if (!selectedLead || !newTask.title.trim()) return;

    const lead = leads.find(l => l.id === selectedLead);
    if (!lead) return;

    const task: Task = {
      id: Date.now(),
      leadId: selectedLead,
      title: newTask.title,
      description: newTask.description,
      dueDate: newTask.dueDate,
      priority: newTask.priority,
      status: 'pending',
      assignedTo: newTask.assignedTo,
      type: newTask.type,
      createdAt: new Date().toISOString()
    };

    const updatedTasks = [...(lead.tasks || []), task];
    onUpdateTasks(selectedLead, updatedTasks);

    // Reset form
    setNewTask({
      title: '',
      description: '',
      dueDate: '',
      priority: 'Medium',
      assignedTo: '',
      type: 'follow-up'
    });
    setShowCreateModal(false);
    setSelectedLead(null);
  };

  const handleUpdateTaskStatus = (leadId: number, taskId: number, newStatus: 'pending' | 'completed' | 'overdue' | 'cancelled') => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead || !lead.tasks) return;

    const updatedTasks = lead.tasks.map(task =>
      task.id === taskId ? { ...task, status: newStatus } : task
    );
    onUpdateTasks(leadId, updatedTasks);
  };

  const handleEditTask = (leadId: number, task: Task) => {
    setEditingTask({ ...task, leadId } as any);
    setNewTask({
      title: task.title,
      description: task.description || '',
      dueDate: task.dueDate,
      priority: task.priority,
      assignedTo: task.assignedTo || '',
      type: task.type || 'follow-up'
    });
    setShowCreateModal(true);
  };

  const handleUpdateTask = () => {
    if (!editingTask || !newTask.title.trim()) return;

    const lead = leads.find(l => l.id === (editingTask as any).leadId);
    if (!lead || !lead.tasks) return;

    const updatedTasks = lead.tasks.map(task =>
      task.id === editingTask.id 
        ? {
            ...task,
            title: newTask.title,
            description: newTask.description,
            dueDate: newTask.dueDate,
            priority: newTask.priority,
            assignedTo: newTask.assignedTo,
            type: newTask.type
          }
        : task
    );

    onUpdateTasks((editingTask as any).leadId, updatedTasks);
    setEditingTask(null);
    setNewTask({
      title: '',
      description: '',
      dueDate: '',
      priority: 'Medium',
      assignedTo: '',
      type: 'follow-up'
    });
    setShowCreateModal(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  const isOverdue = (dateString: string) => {
    return new Date(dateString) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Task Creation */}
      <Card className="border border-[#d9cba6]/30 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-[#d9cba6]/10 to-[#e5d7b3]/10 border-b border-[#d9cba6]/20">
          <CardTitle className="text-[#8b7355] flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Task Management
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-[#d9cba6] hover:bg-[#c9b896] text-[#8b7355]">
                <Plus className="h-4 w-4 mr-2" />
                Create New Task
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-[#d9cba6]/30">
              <DialogHeader>
                <DialogTitle className="text-[#8b7355]">
                  {editingTask ? 'Update Task' : 'Create New Task'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label>Select Lead</Label>
                  <Select 
                    value={selectedLead?.toString() || (editingTask as any)?.leadId?.toString() || ''} 
                    onValueChange={(value) => setSelectedLead(Number(value))}
                    disabled={!!editingTask}
                  >
                    <SelectTrigger className="border-[#d9cba6]/50 focus:border-[#d9cba6]">
                      <SelectValue placeholder="Select a lead" />
                    </SelectTrigger>
                    <SelectContent>
                      {leads.map(lead => (
                        <SelectItem key={lead.id} value={lead.id.toString()}>
                          {lead.companyName} - {lead.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Task Title</Label>
                  <Input
                    value={newTask.title}
                    onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter task title"
                    className="border-[#d9cba6]/50 focus:border-[#d9cba6]"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter task description"
                    className="border-[#d9cba6]/50 focus:border-[#d9cba6]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="border-[#d9cba6]/50 focus:border-[#d9cba6]"
                    />
                  </div>

                  {/* <div>
                    <Label>Priority</Label>
                    <Select value={newTask.priority} onValueChange={(value: 'High' | 'Medium' | 'Low') => setNewTask(prev => ({ ...prev, priority: value }))}>
                      <SelectTrigger className="border-[#d9cba6]/50 focus:border-[#d9cba6]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div> */}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Assigned To</Label>
                    <Input
                      value={newTask.assignedTo}
                      onChange={(e) => setNewTask(prev => ({ ...prev, assignedTo: e.target.value }))}
                      placeholder="Assign to team member"
                      className="border-[#d9cba6]/50 focus:border-[#d9cba6]"
                    />
                  </div>

                  <div>
                    <Label>Task Type</Label>
                    <Select value={newTask.type} onValueChange={(value: 'call' | 'email' | 'meeting' | 'proposal' | 'follow-up' | 'demo') => setNewTask(prev => ({ ...prev, type: value }))}>
                      <SelectTrigger className="border-[#d9cba6]/50 focus:border-[#d9cba6]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="call">Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="follow-up">Follow-up</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="proposal">Proposal</SelectItem>
                        <SelectItem value="demo">Demo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={editingTask ? handleUpdateTask : handleCreateTask}
                    className="bg-[#d9cba6] hover:bg-[#c9b896] text-[#8b7355]"
                  >
                    {editingTask ? 'Update Task' : 'Create Task'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingTask(null);
                      setNewTask({
                        title: '',
                        description: '',
                        dueDate: '',
                        priority: 'Medium',
                        assignedTo: '',
                        type: 'follow-up'
                      });
                    }}
                    className="border-[#d9cba6] text-[#8b7355] hover:bg-[#d9cba6]/10"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Task Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {['pending', 'completed', 'overdue'].map(status => (
          <Card key={status} className="border border-[#d9cba6]/30 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-[#d9cba6]/10 to-[#e5d7b3]/10 border-b border-[#d9cba6]/20">
              <CardTitle className="text-[#8b7355] capitalize flex items-center justify-between">
                {status.replace('-', ' ')}
                <Badge variant="outline" className="border-[#d9cba6] text-[#8b7355]">
                  {getTasksByStatus(status).length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3 max-h-96 overflow-y-auto">
              {getTasksByStatus(status).map(task => (
                <div key={`${task.leadId}-${task.id}`} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900 text-sm">{task.title}</h4>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditTask(task.leadId, task)}
                        className="h-6 w-6 p-0"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      {status !== 'completed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleUpdateTaskStatus(
                            task.leadId, 
                            task.id, 
                            status === 'pending' ? 'completed' : 'completed'
                          )}
                          className="h-6 w-6 p-0 text-green-600"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-600 mb-2">{task.description}</p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Badge className={getPriorityColor(task.priority)}>
                        {task.priority}
                      </Badge>
                      <span className="text-gray-500">{task.leadName}</span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <span className={`${isOverdue(task.dueDate) && status !== 'completed' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        {formatDate(task.dueDate)}
                      </span>
                    </div>
                  </div>
                  
                  {task.assignedTo && (
                    <div className="flex items-center gap-1 mt-2">
                      <User className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500">{task.assignedTo}</span>
                    </div>
                  )}
                </div>
              ))}
              
              {getTasksByStatus(status).length === 0 && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No {status.replace('-', ' ')} tasks
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TaskManager;
